import {FC, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import FooterBasic from 'components/Footers/Basic';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import InputPassword from 'components/Form/InputPassword';
import InputPhoneNumber from 'components/Form/InputPhoneNumber';
import InputSanitized from 'components/Form/InputSanitized';
import {usernameSanitizer} from 'components/Form/InputSanitized/utils';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import SectionHeader from 'components/SectionHeader';
import {H1} from 'components/Typography';
import {useBackend} from 'state/backend';
import {useLocale} from 'state/locale';
import {Backend} from 'types/api';
import {onApiFormError} from 'utils/request/result';
import {SignupBank, SignupDocument, SignupSend} from '../assets/svg';
import {UrlParameterOptions, UserDetailsFormFields} from '../types';
import {usePostUserDetails, UserDetailsFormData} from './services';

export type UserDetailsBackend = Backend<UrlParameterOptions>;

type UserDetailsProps = {
    onSuccess: () => void;
    onUpdate: (formData: Partial<UserDetailsFormFields>) => void;
    prefillData?: Partial<UserDetailsFormFields>;
};

const UserDetails: FC<UserDetailsProps> = ({
    onSuccess,
    onUpdate,
    prefillData = {},
}) => {
    const {data} = useBackend<UserDetailsBackend>();

    const methods = useForm<UserDetailsFormData>({
        defaultValues: {
            email: prefillData.email,
            nameFirst: prefillData.nameFirst,
            nameLast: prefillData.nameLast,
            password: prefillData.password,
            phoneNumber: prefillData.phoneNumber,
            regcode: prefillData.regcode,
            username: prefillData.username,
        },
    });
    const {handleSubmit, watch} = methods;

    const [showRegistrationCode, setRegistrationCode] = useState(
        () => !!prefillData.regcode,
    );

    const {mutateAsync} = usePostUserDetails();
    const languageCode = useLocale();

    const {t} = useTranslation('Profile.Create.UserDetails');

    useEffect(() => {
        const subscription = watch((value) => {
            onUpdate(value);
        });

        return () => subscription.unsubscribe();
    });

    const onSubmit = async (formData: UserDetailsFormData) => {
        const submittedData = {
            ...formData,
            languageCode,
            leadSource: data.leadSource,
            utmCampaign: data.utmCampaign,
            utmContent: data.utmContent,
            utmMedium: data.utmMedium,
            utmSource: data.utmSource,
            utmTerm: data.utmTerm,
        };
        mutateAsync(submittedData)
            .then(() => {
                onSuccess();
            })
            .catch(onApiFormError(methods));
    };

    return (
        <div className="relative flex min-h-screen flex-col bg-grey-100 ">
            <HeaderBasic className="px-8" />
            <DualPage
                pagination={{
                    current: 1,
                    length: 2,
                }}
                size="6xl"
            >
                <FormProvider {...methods}>
                    <form
                        className="rounded border border-grey-300 bg-white p-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            subtitle={t('pageForm.subtitle')}
                            title={t('pageForm.title')}
                        />
                        <Stacked className="mt-4 w-full">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <InputText
                                    className="w-full"
                                    label={t('form:lastName')}
                                    name="nameLast"
                                    required={true}
                                />
                                <InputText
                                    className="w-full"
                                    label={t('form:firstName')}
                                    name="nameFirst"
                                    required={true}
                                />
                            </div>
                            <InputEmail
                                label={t('form:workEmail')}
                                name="email"
                                required={true}
                            />
                            <InputPhoneNumber
                                helpText={t('helpText.phoneNumber')}
                                label={t('form:phoneNumber')}
                                name="phoneNumber"
                                required={true}
                            />
                            <InputSanitized
                                helpText={t('helpText.username')}
                                label={t('form:username')}
                                name="username"
                                required={true}
                                sanitizer={usernameSanitizer}
                            />
                            <InputPassword
                                name="password"
                                required={true}
                                showPasswordRules={true}
                            />
                            {showRegistrationCode ? (
                                <InputText
                                    label={t('form:registrationCode')}
                                    name="regcode"
                                />
                            ) : (
                                <Button
                                    aria-label="Registration code button"
                                    className="whitespace-pre-line text-left"
                                    kind="tertiary"
                                    onClick={() => setRegistrationCode(true)}
                                >
                                    <Trans
                                        components={[
                                            <span
                                                key={0}
                                                className="text-light-primary"
                                            >
                                                {' '}
                                            </span>,
                                        ]}
                                        i18nKey="pageForm.registrationCode"
                                        ns="Profile.Create.UserDetails"
                                        t={t}
                                    />
                                </Button>
                            )}
                            <FormResult />
                            <FormActions>
                                <SubmitButton>
                                    {t('pageForm.letsGetStarted')}
                                </SubmitButton>
                            </FormActions>
                        </Stacked>
                    </form>
                </FormProvider>
                <div className="hidden p-6 md:grid">
                    <SectionHeader
                        className="mt-0"
                        hideHR={true}
                        title={t('information.title')}
                    />
                    <section className="mt-6 flex">
                        <SignupDocument className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">
                                {t('information.content1Title')}
                            </H1>
                            <p>{t('information.content1Content')}</p>
                        </div>
                    </section>
                    <section className="mt-4 flex">
                        <SignupSend className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">
                                {t('information.content2Title')}
                            </H1>
                            <p>{t('information.content2Content')}</p>
                        </div>
                    </section>
                    <section className="mt-4 flex">
                        <SignupBank className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">
                                {t('information.content3Title')}
                            </H1>
                            <p>{t('information.content3Content')}</p>
                        </div>
                    </section>
                </div>
            </DualPage>
            <FooterBasic />
        </div>
    );
};

export default UserDetails;
